import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { ContentHeader, QueryStatus, useToast } from "@cx/ui";
import type { ListResult } from "@cx/contracts";
import { can, type Module, type Permissions } from "@cx/db";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";

type Row = { entityType: string; entityId: string; label: string; deletedAt: string };
const ENTITY: Record<string, string> = {
  contact: "Contato",
  attachment: "Anexo",
  product: "Produto",
  warehouse: "Depósito",
  price_list: "Lista de preço",
  payment_term: "Condição de pagamento",
  seller: "Vendedor",
  commission_rule: "Regra de comissão",
  status_definition: "Situação",
  status_transition: "Transição de situação",
  quote: "Proposta comercial",
  sales_order: "Pedido de venda",
  financial_account: "Caixa ou banco",
  financial_category: "Categoria financeira",
  payment_method: "Forma de pagamento",
};
/** A API exige, além de deletions:restore, o delete do módulo do registro (mesma tabela da rota). */
const MODULE_OF: Record<string, Module> = {
  contact: "contacts",
  attachment: "attachments",
  product: "products",
  warehouse: "warehouses",
  price_list: "price_lists",
  payment_term: "payment_terms",
  seller: "sellers",
  commission_rule: "sellers",
  status_definition: "statuses",
  status_transition: "statuses",
  quote: "quotes",
  sales_order: "sales_orders",
  financial_account: "financial_accounts",
  financial_category: "financial_categories",
  payment_method: "payment_methods",
};

export function DeletionsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canRestore = (entityType: string) =>
    can(perms, "deletions", "restore") &&
    Object.hasOwn(MODULE_OF, entityType) &&
    can(perms, MODULE_OF[entityType]!, "delete");
  const q = useInfiniteQuery({
    queryKey: ["deletions", companyId],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<Row>>(`/deletions${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const restore = useMutation({
    mutationFn: (r: Row) => api(`/deletions/${r.entityType}/${r.entityId}/restore`, { method: "POST" }),
    // Invalida só o que a restauração muda, na empresa ativa (sem invalidateQueries global).
    onSuccess: (_d, r) => {
      toast("success", "Registro restaurado");
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
      if (r.entityType === "contact") {
        qc.invalidateQueries({ queryKey: ["contacts", companyId] });
        qc.invalidateQueries({ queryKey: ["contact", companyId, r.entityId] });
      }
      if (r.entityType === "product") {
        qc.invalidateQueries({ queryKey: ["products", companyId] });
        qc.invalidateQueries({ queryKey: ["product", companyId, r.entityId] });
      }
      if (r.entityType === "attachment") qc.invalidateQueries({ queryKey: ["attachments", companyId] });
      if (r.entityType === "warehouse") qc.invalidateQueries({ queryKey: ["warehouses", companyId] });
      if (r.entityType === "price_list") qc.invalidateQueries({ queryKey: ["price-lists", companyId] });
      if (r.entityType === "payment_term") qc.invalidateQueries({ queryKey: ["payment-terms", companyId] });
      if (r.entityType === "seller") qc.invalidateQueries({ queryKey: ["sellers", companyId] });
      if (r.entityType === "commission_rule")
        qc.invalidateQueries({ queryKey: ["commission-rules", companyId] });
      if (r.entityType === "status_definition" || r.entityType === "status_transition")
        qc.invalidateQueries({ queryKey: ["statuses", companyId] });
      if (r.entityType === "quote") qc.invalidateQueries({ queryKey: ["quotes", companyId] });
      if (r.entityType === "sales_order") qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
      if (r.entityType === "financial_account")
        qc.invalidateQueries({ queryKey: ["fin-accounts", companyId] });
      if (r.entityType === "financial_category")
        qc.invalidateQueries({ queryKey: ["fin-categories", companyId] });
      if (r.entityType === "payment_method")
        qc.invalidateQueries({ queryKey: ["payment-methods", companyId] });
    },
    // 409 duplicate_document/duplicate_sku: restaurar recriaria CPF/CNPJ ou SKU duplicado (decisão 10); o toast mostra a mensagem da API.
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <>
      <ContentHeader title="Consulta de exclusões" breadcrumb={["Preferências", "Consulta de exclusões"]} />
      <div className="box card">
        <QueryStatus loading={q.isLoading} error={q.error} />
        {q.isSuccess && (
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th scope="col">Tipo</th>
                  <th scope="col">Registro</th>
                  <th scope="col">Excluído em</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      Nenhum registro excluído.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={`${r.entityType}:${r.entityId}`}>
                    <td>{ENTITY[r.entityType] ?? r.entityType}</td>
                    <td>{r.label}</td>
                    <td>{dateTime(r.deletedAt)}</td>
                    <td className="text-end">
                      {canRestore(r.entityType) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          aria-label={`Restaurar ${r.label}`}
                          disabled={restore.isPending}
                          onClick={() => restore.mutate(r)}
                        >
                          Restaurar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {q.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={q.isFetchingNextPage}
              onClick={() => q.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
    </>
  );
}
